import Service from '@ember/service';
import ExtensionLogger from '../../ExtensionLogger.ts';
import ExtensionStyles from '../../ExtensionStyles.ts';
import { Extension, type PeekExtensionsAPI } from '../../Extension.ts';
import { action } from '@ember/object';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import {
  extendable,
  extendableProperties
} from '../../decorators/extendable.ts';
import { broadcastToExtensions } from '../../decorators/broadcast-to-extensions.ts';

/**
 * methods from this service needs to be declared as @action so context its bound correctly
 * OR use .bind(this) when referencing local methods
 * other services functions should also be bound, so those functions can target respective services correctly
 * as "this" = this service
 */
export default class PeekExtensionsAPIService extends Service {
  // DO NOT REMOVE THESE @extendable/@broadcastToExtensions properties
  // this is for our text-extension-test.js suite
  @tracked _testNonExtendableProperty = 'this text should not be accessible';
  @tracked @extendable _testExtendableProperty =
    'this text is assigned on extensions-api service';
  @tracked exampleArray: string[] = ['this', 'is', 'an', 'example', 'array'];
  @tracked exampleArrayForMethod: string[] = [
    'this',
    'is',
    'an',
    'example',
    'array',
    'for',
    'methods'
  ];

  @broadcastToExtensions({
    eventName: 'PeekExtensionsAPIService.arrayToBroadcast.update'
  })
  get arrayToBroadcast(): string[] {
    return this.exampleArray;
  }

  @broadcastToExtensions({
    eventName: 'PeekExtensionsAPIService.arrayWithBroadcastDisabled.update',
    disabled: true
  })
  get arrayWithBroadcastDisabled(): string[] {
    return this.exampleArray;
  }

  @broadcastToExtensions({
    eventName: 'PeekExtensionsAPIService.arrayWithBroadcastDisabledById.update',
    disableForInstallIdList: ['disabled-extension-id']
  })
  get arrayWithBroadcastDisabledById(): string[] {
    return this.exampleArray;
  }

  @action
  @broadcastToExtensions({
    eventName: 'PeekExtensionsAPIService.testMethod.update'
  })
  testMethod(value1: string, value2: string): string[] {
    this.exampleArrayForMethod.push(value1, value2);
    return this.exampleArrayForMethod;
  }

  @action
  @broadcastToExtensions({
    eventName: 'PeekExtensionsAPIService.testMethodPromise.update'
  })
  testMethodPromise(): Promise<string[]> {
    return new Promise((resolve) => {
      resolve(this.exampleArrayForMethod);
    });
  }
  // END DO NOT REMOVE

  // to avoid giving access to the service instance, we provide a proxy with only the methods we want to expose
  // to extensions code. NOTE: Be sure to use .bind(this/externalService) to correctly bind context
  @action
  getAPIProxy(logsEnabled: boolean = false): PeekExtensionsAPI {
    if (!ExtensionLogger.enabled && logsEnabled) {
      ExtensionLogger.enabled = logsEnabled;
    }

    return {
      methods: {
        invokeLookup: this.invokeLookup.bind(this)
      },
      styles: {
        customProperties: ExtensionStyles.availableCSSCustomProperties,
        setCSSCustomProperty:
          ExtensionStyles.setCSSCustomProperty.bind(ExtensionStyles)
      },
      logger: {
        log: this.log.bind(this),
        logError: this.logError.bind(this)
      },
      apolloGql: {},
      constants: {}
    };
  }

  log(message: string, ...info: unknown[]): void {
    ExtensionLogger.log(message, ...info);
  }

  logError(message: string, ...info: unknown[]): void {
    ExtensionLogger.logError(message, ...info);
  }

  /**
   * returns a proxy for a service instance, allowing access to only attrs/methods marked with @extendable
   * - attrs: those are allowed to be accessed directly by extensions
   * - methods: as some methods can use "this.attrA" internally, and attrA could not be @extendable, we provide direct
   *    access to methods, but we bind the service to the method, so it can access the target's service attrs/methods
   * @param callerExtension
   * @param serviceName
   * @returns Proxy(service)
   */
  invokeLookup(callerExtension: Extension, serviceName: string): unknown {
    ExtensionLogger.log(
      'Ember - Service::ExtensionsAPI::invokeLookup(): invoking Ember lookup from extension',
      {
        caller: callerExtension,
        arguments: {
          ...arguments
        }
      }
    );

    // @ts-expect-error this is never undefined
    const service = getOwner(this).lookup(`service:${serviceName}`);

    if (!service) {
      ExtensionLogger.logError(
        `Ember - Service::ExtensionsAPI::invokeLookup - Service "service:${serviceName}" not found.`,
        {
          caller: callerExtension,
          arguments: {
            ...arguments
          }
        }
      );

      return;
    }

    // this proxy only allows "get" access to @extendable properties/methods
    // but won't "set" unless we define a setter for properties
    return new Proxy(service, {
      get(target: Service, prop: string | symbol, receiver: unknown) {
        // Allow built-in Symbols without restriction
        if (typeof prop === 'symbol') {
          return Reflect.get(target, prop, receiver);
        }

        // Skip access checks for Ember internals like `__OWNER__`, etc.
        if (typeof prop !== 'string') {
          return Reflect.get(target, prop, receiver);
        }

        const className = target.constructor?.name || 'Unknown';

        const isExtendable =
          extendableProperties.get(className)?.has(prop) ||
          extendableProperties.get('Unknown')?.has(prop);

        if (isExtendable) {
          const targetProp = (target as unknown as Record<string, unknown>)[
            prop
          ];

          // Bind functions to service
          if (typeof targetProp === 'function') {
            return targetProp.bind(target);
          }

          // For classic .get() access, return the computed value
          if (typeof target.get === 'function') {
            return target.get(prop);
          }

          return Reflect.get(target, prop, receiver);
        }

        // Optional: allow access to `.get()` itself
        if (prop === 'get') {
          return target.get?.bind(target);
        }

        // Otherwise: block + log
        ExtensionLogger.logError(
          `Ember - Service::ExtensionsAPI::invokeLookup - Access to ${prop} is restricted. Use the @extendable decorator to expose it.`,
          {
            caller: callerExtension,
            arguments: {
              ...arguments
            }
          }
        );

        // Return undefined instead of crashing
        return undefined;
      }
    });
  }
}
